import { useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    HStack,
    Link,
    VStack,
    Text,
    IconButton,
    Skeleton,
    SkeletonCircle,
    SimpleGrid,
} from "@chakra-ui/react";
import {
    HiOutlineCpuChip, HiStar,
    HiPhone, HiEnvelope, HiClock, HiArrowPath, HiOutlineShieldCheck,
    HiOutlineChevronLeft, HiOutlineChevronRight, HiChevronDown, HiChevronUp,
} from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { CandidateService } from "@/services/CandidateService";
import { toaster } from "@/components/ui/toaster";
import dayjs from "dayjs";
import CandidateStatusBadge from "./components/CandidateStatusBadge";
import CandidateDecisionSection from "./components/candidate/CandidateDecisionSection";
import CandidateDecisionHistory from "./components/candidate/CandidateDecisionHistory";
import CandidateAxisPill from "./components/CandidateAxisPill";
import CandidateAiDataTab from "./components/candidate/CandidateAiDataTab";
import ScoreBreakdown from "./components/candidate/ScoreBreakdown";
import AiRecommendationHeader from "./components/candidate/AiRecommendationHeader";
import useScoreLabels from "./components/candidate/useScoreLabels";
import { ModelService } from "@/services/ModelService";
import CandidateEvaluationTab from "./components/candidate/CandidateEvaluationTab";
import CandidateTimelineTab from "./components/candidate/CandidateTimelineTab";
import CandidateNotesTab from "./components/candidate/CandidateNotesTab";
import CandidateAttachmentsTab from "./components/candidate/CandidateAttachmentsTab";
import CandidateInteractionsTab from "./components/candidate/CandidateInteractionsTab";
import useCandidateSingleActions from "./hooks/useCandidateSingleActions";
import { ScoreBadge, evaluationStatusFromApi, getScoreBucket } from "@/scoringUi";
import { useRecruitmentScoring } from "@/auth/ScoringContext";

const SCORE_CIRC = 2 * Math.PI * 28;

const formatPoints = (v) => (Number.isInteger(Number(v)) ? String(v) : Number(v).toFixed(1));

const CandidateDetailPage = () => {
    const { t } = useTranslation("models");
    const { labels: scoreLabels } = useScoreLabels();
    const { id, recruitmentId, candidateId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Populated by RecruitmentCandidatesPage when navigating in from the list, so this page
    // can offer prev/next through that same (filtered, sorted) page of results.
    const candidateIds = location.state?.candidateIds ?? null;
    const candidateIndex = location.state?.candidateIndex;
    const hasSiblingList = Array.isArray(candidateIds) && candidateIndex >= 0;
    const hasPrevCandidate = hasSiblingList && candidateIndex > 0;
    const hasNextCandidate = hasSiblingList && candidateIndex < candidateIds.length - 1;

    const goToList = () => navigate(`/models/${id}/recruitments/${recruitmentId}/candidates`);
    const goToSibling = (offset) => {
        if (!hasSiblingList) return;
        const newIndex = candidateIndex + offset;
        if (newIndex < 0 || newIndex >= candidateIds.length) return;
        navigate(`/models/${id}/recruitments/${recruitmentId}/candidates/${candidateIds[newIndex]}`, {
            state: { ...location.state, candidateIndex: newIndex },
        });
    };

    const [candidate, setCandidate] = useState(null);
    const [recruitmentScoringEnabled, setRecruitmentScoringEnabled] = useState(false);
    const scoringActive = useRecruitmentScoring(recruitmentScoringEnabled);

    useEffect(() => {
        if (!recruitmentId) return;
        ModelService.getRecruitmentById(recruitmentId)
            .then((r) => setRecruitmentScoringEnabled(!!r?.scoringEnabled))
            .catch(() => setRecruitmentScoringEnabled(false));
    }, [recruitmentId]);
    const [notes, setNotes] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [interactionsTotalCount, setInteractionsTotalCount] = useState(0);
    const [latestConsent, setLatestConsent] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isNotesLoading, setIsNotesLoading] = useState(true);
    const [notesSearch, setNotesSearch] = useState('');
    const [notesSortDirection, setNotesSortDirection] = useState('desc');
    const [debouncedNotesSearch, setDebouncedNotesSearch] = useState('');
    const [notesFetchKey, setNotesFetchKey] = useState(0);
    const [timelineFetchKey, setTimelineFetchKey] = useState(0);
    const [isTimelineLoading, setIsTimelineLoading] = useState(true);
    const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(true);
    const [fullEvaluation, setFullEvaluation] = useState(null);
    const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
    const [scoringModel, setScoringModel] = useState(null);
    const [isFavLoading, setIsFavLoading] = useState(false);
    const [isXlsxLoading, setIsXlsxLoading] = useState(false);
    const [decisionHistoryKey, setDecisionHistoryKey] = useState(0);
    const [decisionHistory, setDecisionHistory] = useState([]);
    const [activeTab, setActiveTab] = useState(location.state?.tab ?? "ai");
    const [highlightId, setHighlightId] = useState(location.state?.highlightId ?? null);

    const { openStatusModal, openEmailModal, openNoteModal, openTimelineModal, modals: singleActionModals } = useCandidateSingleActions({
        t,
        onStatusChanged: (_, updated) => {
            setCandidate(prev => ({ ...prev, status: updated.status, processStatus: updated.processStatus, aiRecommendation: updated.aiRecommendation }));
            setTimelineFetchKey(k => k + 1);
            setDecisionHistoryKey(k => k + 1);
        },
        onNoteAdded: () => setNotesFetchKey(k => k + 1),
        onTimelineEntryAdded: () => setTimelineFetchKey(k => k + 1),
    });

    useSetPageHeader(t("candidateDetailPage.headerTitle"), "", <HiOutlineCpuChip />);

    useEffect(() => {
        CandidateService.getDecisionHistory(candidateId).then(d => setDecisionHistory(d?.items ?? [])).catch(() => setDecisionHistory([]));
    }, [candidateId, decisionHistoryKey]);

    useEffect(() => {
        setIsLoading(true);
        CandidateService.getById(candidateId).then(setCandidate).finally(() => setIsLoading(false));
    }, [candidateId]);

    useEffect(() => {
        const timer = setTimeout(() => { setDebouncedNotesSearch(notesSearch); }, 400);
        return () => clearTimeout(timer);
    }, [notesSearch]);

    useEffect(() => {
        setIsNotesLoading(true);
        CandidateService.getNotes(candidateId, 'createdAt', notesSortDirection, debouncedNotesSearch || null)
            .then(d => setNotes(d ?? []))
            .finally(() => setIsNotesLoading(false));
    }, [candidateId, notesSortDirection, debouncedNotesSearch, notesFetchKey]);

    useEffect(() => {
        setIsTimelineLoading(true);
        CandidateService.getTimeline(candidateId).then(d => setTimeline(d ?? [])).finally(() => setIsTimelineLoading(false));
    }, [candidateId, timelineFetchKey]);

    useEffect(() => {
        setIsAttachmentsLoading(true);
        CandidateService.getAttachments(candidateId).then(d => setAttachments(d ?? [])).finally(() => setIsAttachmentsLoading(false));
    }, [candidateId]);

    useEffect(() => {
        if (!scoringActive || !recruitmentId) return;
        ModelService.getRecruitmentScoringModel(recruitmentId).then(setScoringModel).catch(() => setScoringModel(null));
    }, [recruitmentId, scoringActive]);

    useEffect(() => {
        setFullEvaluation(null);
        setIsBreakdownOpen(false);
        if (!scoringActive) return;
        CandidateService.getEvaluation(candidateId).then(setFullEvaluation).catch(() => setFullEvaluation(null));
    }, [candidateId, scoringActive]);

    const loadLatestConsent = useCallback(() => {
        CandidateService.getConsents(candidateId)
            .then(d => setLatestConsent(Array.isArray(d) && d.length ? d[0] : null))
            .catch(() => setLatestConsent(null));
    }, [candidateId]);

    useEffect(() => {
        setLatestConsent(undefined);
        loadLatestConsent();
    }, [loadLatestConsent]);

    useEffect(() => {
        CandidateService.getInteractions(candidateId, 1, 1).then(d => setInteractionsTotalCount(d?.totalCount ?? 0));
    }, [candidateId]);

    const handleScoreOverridden = (updated) => {
        setFullEvaluation(updated);
        CandidateService.getById(candidateId).then(setCandidate);
        setTimelineFetchKey(k => k + 1);
    };

    const handleToggleFavorite = async () => {
        setIsFavLoading(true);
        const updated = await CandidateService.toggleFavorite(candidateId);
        setCandidate(prev => ({ ...prev, isFavourite: updated.isFavourite }));
        setIsFavLoading(false);
    };


    const handleOpenAttachment = (att) => {
        if (!att.path) return;
        const url = `${process.env.REACT_APP_BASE_URL || '/mlapi'}/Candidate/${candidateId}/attachments/file?name=${encodeURIComponent(att.path)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleDownloadAttachment = async (att) => {
        if (!att.path) return;
        try {
            const response = await CandidateService.downloadAttachment(candidateId, att.path);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = att.name ?? att.path.split('/').pop();
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.statusUpdateError") });
        }
    };

    const handleUploadAttachments = async (files) => {
        const newAtts = await CandidateService.addAttachments(candidateId, files);
        setAttachments(prev => [...prev, ...(newAtts ?? [])]);
    };

    const handleDownloadAllZip = async () => {
        try {
            const response = await CandidateService.downloadAllAttachmentsZip(candidateId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `attachments_${candidateId}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.statusUpdateError") });
        }
    };

    const handleExportXlsx = async () => {
        if (!recruitmentId) return;
        setIsXlsxLoading(true);
        try {
            const response = await CandidateService.exportXlsx(recruitmentId, [candidateId]);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `kandydat_${candidate.firstname}_${candidate.lastname}-${dayjs().format("YYYY-MM-DD")}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.statusUpdateError") });
        } finally {
            setIsXlsxLoading(false);
        }
    };

    const handleHeaderAddNote = () => openNoteModal(candidateId);

    if (isLoading) {
        return (
            <VStack w="full" py={6} gap={4} align="flex-start">
                <Skeleton height="14px" width="160px" borderRadius={4} />
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                    <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                        <VStack align="flex-start" gap={3} flex={1} minW="200px">
                            <Skeleton height="26px" width="200px" borderRadius={6} />
                            <Skeleton height="22px" width="90px" borderRadius="999px" />
                            <Skeleton height="14px" width="140px" borderRadius={4} />
                            <Skeleton height="14px" width="180px" borderRadius={4} />
                            <Skeleton height="14px" width="160px" borderRadius={4} />
                        </VStack>
                        <HStack gap={2}>
                            <Skeleton height="32px" width="100px" borderRadius={8} />
                            <Skeleton height="32px" width="100px" borderRadius={8} />
                            <Skeleton height="32px" width="90px" borderRadius={8} />
                        </HStack>
                    </HStack>
                </Box>
                <HStack gap={4} borderBottom="1px solid" borderColor="gray.200" w="full" pb={3}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} height="14px" width="70px" borderRadius={4} />
                    ))}
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <Box key={i} bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                            <HStack gap={3} mb={4}>
                                <SkeletonCircle size="32px" />
                                <Skeleton height="16px" width="140px" borderRadius={4} />
                            </HStack>
                            <VStack align="stretch" gap={3}>
                                <Skeleton height="12px" width="100%" borderRadius={4} />
                                <Skeleton height="12px" width="80%" borderRadius={4} />
                            </VStack>
                        </Box>
                    ))}
                </SimpleGrid>
            </VStack>
        );
    }

    if (!candidate) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="gray.500">{t("candidateDetailPage.notFound")}</Text>
            </VStack>
        );
    }

    const fullName = [candidate.firstname, candidate.lastname].filter(Boolean).join(" ") || "-";
    const score = candidate.evaluation?.scorePercent ?? null;
    const evaluationStatus = candidate.evaluation ? evaluationStatusFromApi(candidate.evaluation.status) : null;
    const scoreOffset = score != null ? SCORE_CIRC - (Math.min(100, Math.max(0, score)) / 100) * SCORE_CIRC : SCORE_CIRC;
    const scoreBucket = score != null ? getScoreBucket({ scorePercent: score, status: evaluationStatus }) : null;
    const scoreBucketColor = scoreBucket?.color ?? "purple.400";
    const pointsRaw = fullEvaluation?.scoreRaw ?? candidate.evaluation?.scoreRaw ?? null;
    const pointsMax = fullEvaluation?.scoreMax ?? candidate.evaluation?.scoreMax ?? null;
    const hasPoints = pointsRaw != null && pointsMax != null && pointsMax > 0;
    const breakdown = (fullEvaluation?.criterionResults ?? []).filter(r => r.maxScore > 0);
    const scoreRingColor = `var(--chakra-colors-${scoreBucketColor.replace(".", "-")}, #8B5CF6)`;

    const TABS = [
        { key: "ai", label: t("candidateDetailPage.tabAiData") },
        { key: "timeline", label: t("candidateDetailPage.tabTimeline"), count: timeline.length },
        ...(scoringActive ? [{ key: "evaluation", label: t("candidateDetailPage.tabEvaluation") }] : []),
        { key: "notes", label: t("candidateDetailPage.tabNotes"), count: notes.length },
        { key: "attachments", label: t("candidateDetailPage.tabAttachments"), count: attachments.length },
        { key: "decisions", label: t("candidateDetailPage.tabDecisions"), count: decisionHistory.length },
        { key: "interactions", label: t("candidateDetailPage.tabInteractions"), count: interactionsTotalCount },
    ];

    return (
        <>
            <VStack w="full" py={6} gap={4} align="flex-start">

                {/* Back to list / prev-next through the current results page */}
                <HStack w="full" justify="space-between" align="center">
                    <HStack
                        as="button"
                        type="button"
                        onClick={goToList}
                        color="gray.500"
                        fontSize="sm"
                        gap={1}
                        cursor="pointer"
                        _hover={{ color: "gray.700" }}
                    >
                        <HiOutlineChevronLeft size={14} /> {t("candidateDetailPage.backToList")}
                    </HStack>
                    {hasSiblingList && (
                        <HStack gap={2}>
                            <Text color="gray.400" fontSize="xs" whiteSpace="nowrap">
                                {t("candidateDetailPage.siblingPosition", { current: candidateIndex + 1, total: candidateIds.length })}
                            </Text>
                            <IconButton
                                variant="outlined" size="sm"
                                aria-label={t("candidateDetailPage.previousCandidate")}
                                disabled={!hasPrevCandidate}
                                onClick={() => goToSibling(-1)}
                            >
                                <HiOutlineChevronLeft />
                            </IconButton>
                            <IconButton
                                variant="outlined" size="sm"
                                aria-label={t("candidateDetailPage.nextCandidate")}
                                disabled={!hasNextCandidate}
                                onClick={() => goToSibling(1)}
                            >
                                <HiOutlineChevronRight />
                            </IconButton>
                        </HStack>
                    )}
                </HStack>

                {/* Header card */}
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                    <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                        {/* Left: name + contact info */}
                        <VStack align="flex-start" gap={2} flex={1} minW="200px">
                            <HStack gap={2}>
                                <Text fontWeight={700} fontSize="2xl" color="gray.900">{fullName}</Text>
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    aria-label="Ulubione"
                                    color={candidate.isFavourite ? "yellow.400" : "gray.500"}
                                    loading={isFavLoading}
                                    onClick={handleToggleFavorite}
                                >
                                    <HiStar />
                                </IconButton>
                            </HStack>
                            <Box
                                as="button"
                                cursor="pointer"
                                transition="opacity 0.15s"
                                onClick={() => openStatusModal(candidateId, candidate.status)}
                                outline="none"
                            >
                                <CandidateStatusBadge status={candidate.status} />
                            </Box>
                            <HStack gap={1.5} flexWrap="wrap">
                                <CandidateAxisPill axis="process" value={candidate.processStatus} />
                                <CandidateAxisPill axis="aiRecommendation" value={candidate.aiRecommendation} />
                            </HStack>
                            {candidate.phoneNumber && (
                                <HStack gap={2}>
                                    <Box color="gray.500" fontSize="sm" flexShrink={0}><HiPhone /></Box>
                                    <Link href={`tel:${candidate.phoneNumber}`} color="gray.900" fontSize="sm">{candidate.phoneNumber}</Link>
                                </HStack>
                            )}
                            {candidate.email && (
                                <HStack gap={2}>
                                    <Box color="gray.500" fontSize="sm" flexShrink={0}><HiEnvelope /></Box>
                                    <Link href={`mailto:${candidate.email}`} color="gray.900" fontSize="sm">{candidate.email}</Link>
                                </HStack>
                            )}
                            <HStack gap={2}>
                                <Box color="gray.500" fontSize="sm" flexShrink={0}><HiClock /></Box>
                                <Text color="gray.500" fontSize="sm">
                                    {t("candidateDetailPage.fieldStartAt")}:{" "}
                                    <Text as="span" color="gray.900">{dayjs(candidate.createdAt).format("DD.MM.YYYY HH:mm")}</Text>
                                </Text>
                            </HStack>
                            {candidate.updatedAt && (
                                <HStack gap={2}>
                                    <Box color="gray.500" fontSize="sm" flexShrink={0}><HiArrowPath /></Box>
                                    <Text color="gray.500" fontSize="sm">
                                        {t("candidateDetailPage.fieldLastActivity")}:{" "}
                                        <Text as="span" color="gray.900">{dayjs(candidate.updatedAt).format("DD.MM.YYYY HH:mm")}</Text>
                                    </Text>
                                </HStack>
                            )}
                            {latestConsent !== undefined && (
                                <HStack gap={2}>
                                    <Box color={latestConsent?.withdrawnAt ? "red.500" : latestConsent ? "green.500" : "gray.500"} fontSize="sm" flexShrink={0}><HiOutlineShieldCheck /></Box>
                                    <Text color="gray.500" fontSize="sm">
                                        {t("candidateDetailPage.fieldConsents")}:{" "}
                                        <Text as="span" color={latestConsent?.withdrawnAt ? "red.600" : latestConsent ? "green.600" : "gray.900"}>
                                            {latestConsent?.withdrawnAt
                                                ? t("candidateDetailPage.consentsWithdrawn", { date: dayjs(latestConsent.withdrawnAt).format("DD.MM.YYYY HH:mm") })
                                                : latestConsent
                                                ? t("candidateDetailPage.consentsGiven", { date: dayjs(latestConsent.acceptedAt).format("DD.MM.YYYY HH:mm") })
                                                : t("candidateDetailPage.consentsMissing")}
                                        </Text>
                                    </Text>
                                </HStack>
                            )}
                        </VStack>
                        {/* Right: action buttons */}
                        <HStack gap={2} flexWrap="wrap" justify="flex-end">
                            <Button variant="outlined" size="sm" onClick={handleHeaderAddNote}>
                                {t("candidateDetailPage.addNote")}
                            </Button>
                            <Button
                                variant="outlined"
                                size="sm"
                                disabled={!candidate.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate.email)}
                                onClick={() => openEmailModal(candidateId)}
                            >
                                {t("candidateDetailPage.sendMessage")}
                            </Button>
                            <Button
                                variant="outlined"
                                size="sm"
                                loading={isXlsxLoading}
                                disabled={!recruitmentId}
                                onClick={handleExportXlsx}
                            >
                                {t("candidateDetailPage.downloadXlsx")}
                            </Button>
                        </HStack>
                    </HStack>
                </Box>

                {/* AI Score card */}
                {scoringActive && score != null && (
                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
                        <HStack gap={6} align="center">
                            <Box position="relative" w="72px" h="72px" flexShrink={0}>
                                <svg viewBox="0 0 72 72" width="72" height="72">
                                    <circle cx="36" cy="36" r="28" fill="none" stroke="#E7E4F0" strokeWidth="5" />
                                    <circle
                                        cx="36" cy="36" r="28"
                                        fill="none"
                                        stroke={scoreRingColor}
                                        strokeWidth="5"
                                        strokeDasharray={SCORE_CIRC}
                                        strokeDashoffset={scoreOffset}
                                        strokeLinecap="round"
                                        transform="rotate(-90 36 36)"
                                    />
                                </svg>
                                <Box
                                    position="absolute"
                                    inset={0}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Text color="gray.900" fontWeight={700} fontSize="lg" lineHeight={1}>
                                        {Math.round(score)}
                                    </Text>
                                    <Text color="gray.500" fontSize="2xs">%</Text>
                                </Box>
                            </Box>
                            <VStack align="flex-start" gap={2} flex={1}>
                                <AiRecommendationHeader />
                                <ScoreBadge evaluation={{ scorePercent: score, status: evaluationStatus }} labels={scoreLabels} size="sm" />
                                <HStack gap={3} w="full" align="center">
                                    <Box h="8px" bg="gray.100" borderRadius="full" flex={1} overflow="hidden">
                                        <Box
                                            h="full"
                                            bg={scoreBucketColor}
                                            w={`${Math.round(score)}%`}
                                            borderRadius="full"
                                            transition="width 0.4s ease"
                                        />
                                    </Box>
                                    <Text color="gray.500" fontSize="xs" flexShrink={0}>{Math.round(score)}%</Text>
                                </HStack>
                                {hasPoints && (
                                    <Text color="gray.700" fontSize="sm">
                                        {t("candidateDetailPage.scorePointsSummary", { raw: formatPoints(pointsRaw), max: formatPoints(pointsMax), percent: Math.round(score) })}
                                    </Text>
                                )}
                                {breakdown.length > 0 && (
                                    <Button size="xs" variant="plain" color="purple.600" px={0} h="auto" onClick={() => setIsBreakdownOpen(o => !o)}>
                                        {isBreakdownOpen ? <HiChevronUp /> : <HiChevronDown />}
                                        {t(isBreakdownOpen ? "candidateDetailPage.scoreHideBreakdown" : "candidateDetailPage.scoreShowBreakdown")}
                                    </Button>
                                )}
                                {isBreakdownOpen && breakdown.length > 0 && <ScoreBreakdown rows={breakdown} model={scoringModel} candidateId={candidateId} onChanged={handleScoreOverridden} />}
                            </VStack>
                        </HStack>

                    </Box>
                )}

                <CandidateDecisionSection
                    candidate={candidate}
                    onDecided={(updated) => {
                        setCandidate(prev => ({ ...prev, ...updated }));
                        setTimelineFetchKey(k => k + 1);
                        setNotesFetchKey(k => k + 1);
                        setDecisionHistoryKey(k => k + 1);
                    }}
                />

                {/* Tabs */}
                <Box w="full">
                    <HStack gap={0} borderBottom="1px solid" borderColor="gray.200" w="full">
                        {TABS.map(tab => (
                            <Box
                                key={tab.key}
                                as="button"
                                display="flex"
                                alignItems="center"
                                gap="6px"
                                px={4}
                                py={3}
                                cursor="pointer"
                                color={activeTab === tab.key ? "gray.900" : "gray.500"}
                                borderBottomWidth="2px"
                                borderBottomStyle="solid"
                                borderBottomColor={activeTab === tab.key ? "purple.500" : "transparent"}
                                fontWeight={activeTab === tab.key ? 600 : 400}
                                fontSize="sm"
                                onClick={() => setActiveTab(tab.key)}
                                transition="all 0.15s"
                                mb="-1px"
                                bg="transparent"
                                outline="none"
                                _hover={{ color: "gray.900" }}
                                userSelect="none"
                            >
                                <Text as="span" fontSize="sm" fontWeight="inherit">{tab.label}</Text>
                                {tab.count > 0 && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        minW="18px"
                                        h="18px"
                                        px="5px"
                                        borderRadius="full"
                                        fontSize="2xs"
                                        fontWeight={600}
                                        bg={activeTab === tab.key ? "purple.100" : "gray.100"}
                                        color={activeTab === tab.key ? "purple.700" : "gray.600"}
                                    >
                                        {tab.count}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </HStack>

                    <Box pt={4}>
                        {scoringActive && activeTab === "evaluation" && (
                            <CandidateEvaluationTab candidateId={candidateId} recruitmentId={recruitmentId} />
                        )}
                        {activeTab === "ai" && (
                            <CandidateAiDataTab candidate={candidate} onOpenDocument={handleOpenAttachment} onConsentChanged={loadLatestConsent} />
                        )}
                        {activeTab === "timeline" && (
                            <CandidateTimelineTab timeline={timeline} isLoading={isTimelineLoading} candidateId={candidateId} onAddEntry={() => openTimelineModal(candidateId)} highlightId={highlightId} onHighlightClear={() => setHighlightId(null)} />
                        )}
                        {activeTab === "notes" && (
                            <CandidateNotesTab
                                notes={notes}
                                isLoading={isNotesLoading}
                                onAddNote={handleHeaderAddNote}
                                candidateId={candidateId}
                                search={notesSearch}
                                onSearchChange={setNotesSearch}
                                sortDirection={notesSortDirection}
                                onToggleSortDirection={() => setNotesSortDirection(d => d === 'desc' ? 'asc' : 'desc')}
                                highlightId={highlightId}
                                onHighlightClear={() => setHighlightId(null)}
                            />
                        )}
                        {activeTab === "attachments" && (
                            <CandidateAttachmentsTab
                                attachments={attachments}
                                isLoading={isAttachmentsLoading}
                                onOpenAttachment={handleOpenAttachment}
                                onDownloadAttachment={handleDownloadAttachment}
                                onUploadAttachments={handleUploadAttachments}
                                onDownloadAllZip={handleDownloadAllZip}
                            />
                        )}
                        {activeTab === "decisions" && <CandidateDecisionHistory items={decisionHistory} />}
                        {activeTab === "interactions" && (
                            <CandidateInteractionsTab candidateId={candidateId} />
                        )}
                    </Box>
                </Box>
            </VStack>

            {singleActionModals}
        </>
    );
};

export default CandidateDetailPage;
